/**
 * @fileoverview Regras puras da tela "Abordagens inteligentes": o formulário do
 * fluxo (a partir de um fluxo salvo ou em branco), o que vai para a API, o
 * primeiro problema que impede salvar e a sequência em texto curto (o horário
 * de envio está em utils/smartApproachText). Testadas em
 * __tests__/flowForm.test.js.
 */

import { extractTemplateVars } from "../../utils/templateVars";
import { missingVariable, templateBodyText } from "../../utils/metaTemplateRules";

export const MAX_MESSAGES = 3;
export const MIN_HOURS = 1;
export const MAX_HOURS = 168;
export const DEFAULT_DELAYS = [4, 8];
export const DEFAULT_ATTRIBUTION_HOURS = 24;
export const DEFAULT_WINDOW = { days: [1, 2, 3, 4, 5, 6], start: "08:00", end: "20:00" };

const emptyStep = (delayHours) => ({ template: null, delayHours: String(delayHours) });

/** Formulário de um fluxo novo (com a conexão, se só há uma). */
export const emptyForm = (whatsappId = "") => ({
  name: "",
  whatsappId,
  initial: null,
  count: MAX_MESSAGES,
  steps: DEFAULT_DELAYS.map(emptyStep),
  sendWindow: { ...DEFAULT_WINDOW, days: [...DEFAULT_WINDOW.days] },
  attributionHours: String(DEFAULT_ATTRIBUTION_HOURS),
  active: true,
});

/** Formulário de um fluxo salvo. As etapas que não existem ficam com o padrão. */
export const formFromFlow = (flow) => ({
  name: flow.name || "",
  whatsappId: flow.whatsappId || "",
  initial: flow.initialTemplateName ? { name: flow.initialTemplateName, language: flow.initialTemplateLanguage } : null,
  count: 1 + (flow.followUps || []).length,
  steps: DEFAULT_DELAYS.map((delay, i) => {
    const f = (flow.followUps || [])[i];
    return f
      ? { template: { name: f.templateName, language: f.language, variables: { ...(f.variables || {}) } }, delayHours: String(f.delayHours) }
      : emptyStep(delay);
  }),
  sendWindow: { ...(flow.sendWindow || DEFAULT_WINDOW), days: [...((flow.sendWindow || DEFAULT_WINDOW).days || [])] },
  attributionHours: String(flow.attributionHours || DEFAULT_ATTRIBUTION_HOURS),
  active: !!flow.active,
});

/** O corpo que vai para POST/PUT /smart-approaches. */
export const payloadFromForm = (form) => ({
  name: form.name.trim(),
  whatsappId: Number(form.whatsappId) || null,
  initialTemplateName: form.initial?.name || "",
  initialTemplateLanguage: form.initial?.language || "",
  followUps: form.steps.slice(0, form.count - 1).map((s) => ({
    templateName: s.template?.name || "",
    language: s.template?.language || "",
    delayHours: Number(s.delayHours),
    variables: { ...(s.template?.variables || {}) },
  })),
  sendWindow: form.sendWindow,
  attributionHours: Number(form.attributionHours),
  active: form.active,
});

const isHours = (value) => {
  const n = Number(value);
  return String(value).trim() !== "" && Number.isInteger(n) && n >= MIN_HOURS && n <= MAX_HOURS;
};

const INITIAL_PREFIX = "initial.";

/**
 * Variáveis que apontam para uma {{N}} que o template inicial não tem mais
 * (trocou o template inicial) ficam sem valor: a tela pede para escolher de novo.
 */
export const dropMissingInitial = (variables, initialVariables) =>
  Object.fromEntries(
    Object.entries(variables || {}).map(([n, source]) =>
      source && source.startsWith(INITIAL_PREFIX) && !initialVariables.includes(source.slice(INITIAL_PREFIX.length))
        ? [n, ""]
        : [n, source]
    )
  );

/**
 * Primeiro problema que impede salvar (null: pode salvar). `t` traduz as
 * chaves de `smartApproaches.editor.problems`; `initialVariables`: as {{N}} do
 * template inicial (as que a 2ª e a 3ª mensagem podem repetir).
 */
export const formProblem = (form, t, initialVariables = []) => {
  if (!form.name.trim()) return t("name");
  if (!form.whatsappId) return t("connection");
  if (!form.initial) return t("initial");
  for (let i = 0; i < form.count - 1; i += 1) {
    const step = form.steps[i];
    const n = i + 2;
    if (!isHours(step.delayHours)) return t("hours", { n, min: MIN_HOURS, max: MAX_HOURS });
    if (!step.template) return t("template", { n });
    const missing = missingVariable({ variables: dropMissingInitial(step.template.variables, initialVariables) });
    if (missing) return t(missing.kind, { n, variable: missing.n });
  }
  if (!form.sendWindow.days.length) return t("days");
  if (!(form.sendWindow.start < form.sendWindow.end)) return t("window");
  if (!isHours(form.attributionHours)) return t("attribution", { min: MIN_HOURS, max: MAX_HOURS });
  return null;
};

/** Variáveis ({{1}}, {{2}}...) do corpo de um template da Meta. */
export const bodyVariables = (tpl) => extractTemplateVars(templateBodyText(tpl));

/**
 * O formulário como vai para a API, sem as variáveis ainda sem valor: é o que
 * conta para "alterações não salvas" (o seletor de variáveis completa as que o
 * rascunho salvo não tinha, e isso não é mudança de quem edita).
 */
export const comparableForm = (form) => {
  const payload = payloadFromForm(form);
  return {
    ...payload,
    followUps: payload.followUps.map((f) => ({
      ...f,
      variables: Object.fromEntries(Object.entries(f.variables).filter(([, source]) => !!source)),
    })),
  };
};

/**
 * A sequência numa linha: "agora → +4 h → +8 h". `t` traduz as chaves de
 * `smartApproaches.sequence`.
 */
export const sequenceText = (flow, t) =>
  [t("now"), ...(flow.followUps || []).map((f) => t("after", { hours: f.delayHours }))].join(" → ");
